import React from "react";
import { CreditCard, Smartphone, ShieldCheck, RefreshCw, Users, Zap, Wifi } from "lucide-react";
import { PageHero, Section, Container, Eyebrow, H2, Lead, CTAButton, GlassCard, Orbs } from "../components/PageUI";
import SEO from "../components/SEO";

const highlights = [
  "No separate card reader or terminal needed",
  "Works with a compatible NFC-enabled phone",
  "Accepts supported cards and mobile wallets",
  "Every donation syncs instantly to your dashboard",
];

const steps = [
  { icon: Smartphone, title: "Open the app", desc: "An authorized staff member opens Tap & Pay from the Masjid Suite app and enters the donation amount." },
  { icon: Wifi, title: "Tap to collect", desc: "Hold the donor's card or phone near the back of the device to complete the contactless payment." },
  { icon: RefreshCw, title: "Instant receipt & sync", desc: "The donation appears on your dashboard immediately, and the donor gets a digital receipt." },
];

const features = [
  { icon: CreditCard, title: "Cards & mobile wallets", desc: "Accept eligible credit and debit cards, Apple Pay, and Google Pay, depending on device and country." },
  { icon: Zap, title: "No extra hardware", desc: "Skip the cost and setup of a separate card reader. It runs on the phone your staff already carry." },
  { icon: ShieldCheck, title: "Secure & PCI compliant", desc: "Payments are processed securely through Stripe. Card details are never stored on Masjid Suite servers." },
  { icon: RefreshCw, title: "Syncs with your dashboard", desc: "Every tap appears on the Admin dashboard in real time, right alongside kiosk and online donations." },
  { icon: Users, title: "Perfect for Jumu'ah & events", desc: "Collect Sadaqah during Jumu'ah, community events, fundraising campaigns, or outreach activities." },
  { icon: Smartphone, title: "NFC powered", desc: "Uses your phone's built-in NFC, no dongle, sleeve, or additional reader required." },
];

export default function TapPayApp() {
  return (
    <div>
      <SEO
        title="Tap & Pay App | Contactless Donations | Masjid Suite"
        description="Turn any staff phone into a secure donation collection point. Accept contactless cards and mobile wallets anywhere in the masjid, no extra hardware required."
        path="/tap-and-pay"
      />
      <PageHero
        eyebrow="Tap & Pay App"
        title="Turn Any Staff Phone Into"
        gradient="A Donation Point."
        lead={<>Turn any authorized staff member's phone into a secure donation collection point, allowing your masjid to accept contactless contributions anytime, anywhere.</>}
        cta={<><CTAButton to="/contact">Get Started Free</CTAButton><CTAButton to="#how-it-works" variant="secondary">See How It Works</CTAButton></>}
      />

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative flex justify-center">
              <div className="w-[280px] md:w-[320px] aspect-[9/19] rounded-[48px] glass-frosted shadow-apple-lg overflow-hidden p-3 border border-white/70">
                <div className="h-full w-full rounded-[36px] bg-gradient-to-br from-[#1e4218] to-[#0e2408] relative overflow-hidden p-5 flex flex-col">
                  <div className="text-white/70 text-[11px] uppercase tracking-widest">Tap & Pay</div>
                  <div className="text-white text-3xl font-display font-semibold mt-1">$50.00</div>
                  <div className="mt-auto mb-16 rounded-2xl bg-white/15 backdrop-blur-md p-5 flex flex-col items-center gap-2">
                    <Wifi size={36} className="text-white" />
                    <span className="text-white/85 text-xs text-center">Hold card or phone near the top</span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 rounded-2xl bg-white/95 p-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#0e2408] text-white flex items-center justify-center"><CreditCard size={14}/></div>
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-[#6b7280]">Ready to collect</div>
                      <div className="text-[13px] font-semibold text-[#1d1d1f]">Waiting for tap</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <Eyebrow>No extra hardware</Eyebrow>
              <H2 gradient="Already Have.">Built Into the App You</H2>
              <div className="mt-6"><Lead>Tap & Pay lives inside the same Masjid Suite app your staff already use, so there's nothing new to buy or carry to start collecting donations.</Lead></div>
              <ul className="mt-8 space-y-3 text-[15px] text-[#4b5563]">
                {highlights.map((t, i) => (
                  <li key={i} className="flex items-start gap-2"><span className="w-1.5 h-1.5 rounded-full bg-[#c9a34a] mt-2.5 shrink-0" /> {t}</li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </Section>

      <Section id="how-it-works" className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="in 3 Taps">Collect a Donation</H2>
            <div className="mt-6 flex justify-center"><Lead>From opening the app to a synced receipt, in seconds.</Lead></div>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <GlassCard key={i}>
                  <div className="text-[#a8862f] font-display text-4xl font-semibold mb-3">0{i + 1}</div>
                  <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4"><Icon size={20} /></div>
                  <h3 className="font-display text-xl text-[#1d1d1f] font-semibold mb-2">{s.title}</h3>
                  <p className="text-[14px] text-[#4b5563] leading-relaxed">{s.desc}</p>
                </GlassCard>
              );
            })}
          </div>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="text-center mb-14">
            <H2 gradient="Designed for Community Giving.">Built for Masjids.</H2>
            <div className="mt-6 flex justify-center"><Lead>Everything your staff need to collect contactless donations, wherever they are.</Lead></div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <GlassCard key={i}>
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1e4218] to-[#0e2408] text-white flex items-center justify-center mb-4 shadow-md"><Icon size={22} /></div>
                  <h3 className="font-display text-lg text-[#1d1d1f] font-semibold mb-2">{f.title}</h3>
                  <p className="text-[13px] text-[#4b5563] leading-relaxed">{f.desc}</p>
                </GlassCard>
              );
            })}
          </div>
        </Container>
      </Section>

      <Section className="bg-hero-light overflow-hidden">
        <Orbs />
        <Container>
          <GlassCard hover={false} className="p-10 md:p-14 text-center">
            <Eyebrow>Not the same as Mobile POS</Eyebrow>
            <H2 gradient="Need a Dedicated Card Reader?">Prefer Physical Hardware?</H2>
            <div className="mt-6 flex justify-center"><Lead>Tap & Pay uses a compatible NFC-enabled phone with no separate card reader. If your masjid wants a dedicated handheld reader instead, Mobile POS is available to buy or rent on the Hardware & Pricing page.</Lead></div>
            <div className="mt-10 flex justify-center"><CTAButton to="/pricing" variant="secondary">View Mobile POS</CTAButton></div>
          </GlassCard>
        </Container>
      </Section>

      <Section className="bg-section-alt overflow-hidden">
        <Container>
          <div className="glass-frosted rounded-[40px] p-10 md:p-14 text-center shadow-apple-lg">
            <H2 gradient="Anytime, Anywhere.">Start Collecting Donations</H2>
            <div className="mt-6 flex justify-center"><Lead>Join masjids using Tap & Pay to make giving easier for their community.</Lead></div>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <CTAButton to="/contact">Get Started Free</CTAButton>
              <CTAButton to="/software#free-apps" variant="secondary">See All Free Apps</CTAButton>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
